import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import SEO from "@/components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "@/components/Breadcrumbs";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, Star } from "lucide-react";

function HeroBlock({ content }) {
  return (
    <section
      className="relative min-h-[350px] flex items-center justify-center text-center px-4 bg-cover bg-center"
      style={{ backgroundImage: content.bg_image ? `url(${content.bg_image})` : undefined, backgroundColor: content.bg_image ? undefined : "#006666" }}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative z-10 text-white max-w-3xl">
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-3">{content.heading || "Heading"}</h1>
        {content.subheading && <p className="text-lg opacity-90 mb-6">{content.subheading}</p>}
        {content.cta_text && content.cta_link && (
          <Link to={content.cta_link} className="inline-block bg-saffron hover:bg-saffron-dark text-white px-8 py-3 rounded-full font-semibold transition-colors">
            {content.cta_text}
          </Link>
        )}
      </div>
    </section>
  );
}

function TextBlock({ content }) {
  return (
    <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {content.heading && <h2 className="font-display text-2xl sm:text-3xl mb-4">{content.heading}</h2>}
      <div className="prose dark:prose-invert max-w-none text-muted-foreground leading-relaxed whitespace-pre-wrap">{content.body}</div>
    </section>
  );
}

function GalleryBlock({ content }) {
  const imgs = content.images || [];
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {content.heading && <h2 className="font-display text-2xl sm:text-3xl mb-6 text-center">{content.heading}</h2>}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {imgs.map((src, i) => (
          <img key={i} src={src} alt="" className="w-full aspect-[4/3] object-cover rounded-xl" />
        ))}
      </div>
    </section>
  );
}

function CTABlock({ content }) {
  return (
    <section className="py-16 px-4 text-center" style={{ backgroundColor: content.bg_color || "#006666" }}>
      <div className="max-w-2xl mx-auto">
        <h2 className="font-display text-2xl sm:text-3xl text-white mb-3">{content.heading || "Ready?"}</h2>
        {content.description && <p className="text-white/80 mb-6">{content.description}</p>}
        {content.button_text && content.button_link && (
          <Link to={content.button_link} className="inline-block bg-white text-gray-900 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors">
            {content.button_text}
          </Link>
        )}
      </div>
    </section>
  );
}

function FAQBlock({ content }) {
  const [openIdx, setOpenIdx] = useState(null);
  let items = [];
  try { items = JSON.parse(content.items_json || "[]"); } catch { items = []; }
  return (
    <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {content.heading && <h2 className="font-display text-2xl sm:text-3xl mb-6 text-center">{content.heading}</h2>}
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="border border-border rounded-lg overflow-hidden">
            <button onClick={() => setOpenIdx(openIdx === i ? null : i)} className="w-full flex justify-between items-center px-5 py-3 text-left hover:bg-muted/40 text-sm font-medium">
              <span>{it.q}</span>
              {openIdx === i ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {openIdx === i && <div className="px-5 pb-3 text-sm text-muted-foreground border-t border-border pt-2">{it.a}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}

function TestimonialsBlock({ content }) {
  let items = [];
  try { items = JSON.parse(content.items_json || "[]"); } catch { items = []; }
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {content.heading && <h2 className="font-display text-2xl sm:text-3xl mb-6 text-center">{content.heading}</h2>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((t, i) => (
          <div key={i} className="border border-border rounded-xl p-5 bg-card">
            <div className="flex gap-0.5 mb-2">{Array.from({ length: t.rating || 5 }, (_, j) => <Star key={j} className="w-4 h-4 fill-saffron text-saffron" />)}</div>
            <p className="text-sm text-muted-foreground mb-3">{t.text}</p>
            <p className="text-xs font-medium">— {t.name}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function HTMLBlock({ content }) {
  return <section className="max-w-5xl mx-auto px-4" dangerouslySetInnerHTML={{ __html: content.html || "" }} />;
}

const RENDERER = {
  hero: HeroBlock,
  text: TextBlock,
  gallery: GalleryBlock,
  cta: CTABlock,
  faq: FAQBlock,
  testimonials: TestimonialsBlock,
  html: HTMLBlock,
};

export default function CMSPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();
  const siteName = settings.site_name || "HyderabadTourism.in";

  useEffect(() => {
    api.get(`/pages/slug/${slug}`)
      .then((r) => setPage(r.data))
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="max-w-5xl mx-auto px-4 py-12"><div className="h-64 bg-muted animate-pulse rounded-xl" /></div>;
  if (!page) return <div className="text-center py-20 text-muted-foreground">Page not found</div>;

  const breadcrumbItems = [{ label: "Home", to: "/" }, { label: page.title }];
  const blocks = [...(page.blocks || [])].sort((a, b) => a.order - b.order);

  return (
    <div data-testid="cms-page">
      <SEO
        title={page.seo_title || page.title}
        description={page.seo_description || `${page.title} — ${siteName}`}
        schemas={[buildBreadcrumbSchema(breadcrumbItems)]}
      />
      {blocks[0]?.type !== "hero" && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <Breadcrumbs items={breadcrumbItems} />
          <h1 className="font-display text-3xl sm:text-4xl mb-6">{page.title}</h1>
        </div>
      )}
      {blocks.map((block, i) => {
        const Comp = RENDERER[block.type];
        return Comp ? <Comp key={i} content={block.content || {}} /> : null;
      })}
    </div>
  );
}
